import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import api from "../../api/api";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Button, CircularProgress, Box } from "@mui/material";
import Alerts from "../Alerts";
import AddIcon from "@mui/icons-material/Add";

export default function Create({ setOption }) {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [warning, setWarning] = useState({});
  const [loadingVideos, setLoadingVideos] = useState({}); // Estado para controlar o loading de cada vídeo
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      carousel: [{ href: "", imgUrl: "", imgUrlMobile: "" }],
      type: "STANDARD", // Valor padrão para o tipo, se necessário
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "carousel",
  });

  const type = watch("type"); // Assistindo o campo 'type' para mudanças
  const videoUrl = watch("videoUrl");
  const standardImgUrl = watch("imgUrl");
  const standardImgUrlMobile = watch("imgUrlMobile");

  // Função para extrair o ID do Vimeo da URL ou retornar o próprio valor se já for um ID
  const getVimeoVideoId = (url) => {
    if (!url) return null;

    // Se for apenas um número, assume que é o ID do Vimeo
    if (/^\d+$/.test(url.trim())) {
      return url.trim();
    }

    // Tenta extrair o ID de diferentes formatos de URL do Vimeo
    const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : null;
  };

  const vimeoVideoId = getVimeoVideoId(videoUrl);

  // Função para gerenciar o loading do vídeo
  const handleVideoLoad = (index) => {
    setLoadingVideos((prev) => ({ ...prev, [index]: false }));
  };

  const onSubmit = (data) => {
    api
      .post("banners", data)
      .then(() => {
        setOption("view");
        handleOpenSnackbar();
        setWarning({
          type: "success",
          message: "Banner criado com sucesso",
        });
      })
      .catch((error) => {
        console.error("Erro ao criar banner:", error);
        handleOpenSnackbar();
        setWarning({
          type: "error",
          message: "Erro ao criar banner",
        });
      });
  };

  const handleOpenSnackbar = () => {
    setOpenSnackbar(true);
  };
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <Button
        onClick={() => setOption("view")}
        variant="outlined"
        color="primary"
        sx={{ mb: 5 }}
      >
        <ArrowBackIcon />
        Voltar
      </Button>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 flex flex-col gap-4"
      >
        {/* Tipo */}
        <div>
          <label
            htmlFor="type"
            className="block text-sm font-medium text-gray-700"
          >
            Tipo
          </label>
          <select
            {...register("type", { required: "Este campo é obrigatório" })}
            className="mt-1 block w-full p-2 border rounded-md"
          >
            <option value="STANDARD">STANDARD</option>
            <option value="CAROUSEL">CAROUSEL</option>
          </select>
          {errors.type && (
            <span className="text-red-500">{errors.type.message}</span>
          )}
          <p className="text-xs text-gray-500">Escolha o tipo de conteúdo.</p>
        </div>

        {/* Carousel */}
        {type === "CAROUSEL" && (
          <div className="mb-4 mt-4">
            <span className="text-md text-center mb-4">
              Imagens do Carrosel
            </span>
            {fields.map((field, index) => {
              // Observa cada campo independentemente para garantir reatividade
              const carouselImgUrl = watch(`carousel.${index}.imgUrl`);
              const carouselImgUrlMobile = watch(
                `carousel.${index}.imgUrlMobile`
              );
              const carouselVimeoId = getVimeoVideoId(carouselImgUrl);
              const isLoadingVideo = loadingVideos[index] === true;

              return (
                <div
                  key={field.id}
                  className="flex space-x-2 items-center p-4 mb-4 bg-gray-100 rounded-md mt-4"
                >
                  <div className="flex-1">
                    <label
                      htmlFor={`carousel[${index}].href`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      Href
                    </label>
                    <input
                      type="text"
                      placeholder="Ex:
                    produtos?category=categoryID"
                      {...register(`carousel.${index}.href`)}
                      className="mt-1 block w-full p-2 border rounded-md"
                    />
                    <p className="text-xs text-gray-500">
                      Caminho de redirecionamento, este categoryID pode ser
                      retirado do próprio site, ou na seção de categorias.
                    </p>
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor={`carousel[${index}].imgUrl`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      Imagem Desktop ou ID do Vimeo
                    </label>
                    <input
                      type="text"
                      placeholder="URL da imagem ou ID do Vimeo (ex: 123456789)"
                      className="mt-1 block w-full p-2 border rounded-md"
                      {...(() => {
                        const { onChange, ...rest } = register(
                          `carousel.${index}.imgUrl`
                        );
                        return {
                          ...rest,
                          onChange: (e) => {
                            onChange(e);
                            const value = e.target.value;
                            const vimeoId = getVimeoVideoId(value);
                            if (vimeoId) {
                              setLoadingVideos((prev) => ({
                                ...prev,
                                [index]: true,
                              }));
                            } else {
                              setLoadingVideos((prev) => ({
                                ...prev,
                                [index]: false,
                              }));
                            }
                          },
                        };
                      })()}
                    />
                    <p className="text-xs text-gray-500">
                      Insira a URL da imagem desktop ou ID do Vimeo.
                    </p>
                    {carouselImgUrl?.trim() && carouselVimeoId && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-700 mb-2">
                          Preview do Vídeo Vimeo:
                        </p>
                        <div
                          className="relative w-full max-w-2xl"
                          style={{ paddingBottom: "56.25%" }}
                        >
                          {isLoadingVideo && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-300 z-10">
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <CircularProgress size={40} />
                                <p className="text-sm text-gray-600">
                                  Carregando vídeo...
                                </p>
                              </Box>
                            </div>
                          )}
                          <iframe
                            src={`https://player.vimeo.com/video/${carouselVimeoId}`}
                            title={`Preview vídeo Vimeo ${index + 1}`}
                            className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg border border-gray-300"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            onLoad={() => handleVideoLoad(index)}
                            style={{
                              opacity: isLoadingVideo ? 0 : 1,
                              transition: "opacity 0.3s",
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {carouselImgUrl?.trim() && !carouselVimeoId && (
                      <div className="mt-2">
                        <img
                          src={carouselImgUrl}
                          alt={`Preview ${index + 1}`}
                          className="max-w-xs h-24 object-cover rounded border border-gray-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor={`carousel[${index}].imgUrlMobile`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      Imagem Mobile (800x839)
                    </label>
                    <input
                      type="text"
                      placeholder="URL da imagem mobile (800x839)"
                      {...(() => {
                        const { onChange, ...rest } = register(
                          `carousel.${index}.imgUrlMobile`
                        );
                        return {
                          ...rest,
                          onChange: (e) => {
                            onChange(e);
                            // Força atualização do preview ao digitar
                          },
                        };
                      })()}
                      className="mt-1 block w-full p-2 border rounded-md"
                    />
                    <p className="text-xs text-gray-500">
                      Insira a URL da imagem mobile.
                    </p>
                    {carouselImgUrlMobile && carouselImgUrlMobile.trim() && (
                      <div className="mt-2">
                        <img
                          src={carouselImgUrlMobile.trim()}
                          alt={`Preview Mobile ${index + 1}`}
                          className="max-w-xs h-24 object-cover rounded border border-gray-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="px-4 bg-red-500 text-white rounded-md h-10 text-center mt-2"
                  >
                    Remover
                  </button>
                </div>
              );
            })}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => append({ href: "", imgUrl: "" })}
                className="px-4 py-2 bg-blue-500 text-white rounded-md flex items-center gap-2"
              >
                <AddIcon />
                Adicionar mais
              </button>
            </div>
          </div>
        )}

        {/* Campos para banner STANDARD */}
        {type !== "CAROUSEL" && (
          <>
            <div className="w-full">
              <label
                htmlFor="position"
                className="block text-sm font-medium text-gray-700"
              >
                Posição do Banner
              </label>
              <select
                {...register("position", {
                  required: "Este campo é obrigatório",
                })}
                className="block w-full p-2 border rounded-md"
              >
                <option value="TOP">TOP</option>
                <option value="MID">MID</option>
                <option value="BOTTOM">BOTTOM</option>
              </select>{" "}
              <p className="text-xs text-gray-500">
                Posições dos banners na Home
              </p>
              {errors.position && (
                <span className="text-red-500">{errors.position.message}</span>
              )}
            </div>

            <div className="flex gap-2 w-full">
              <div className="w-1/2">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700"
                >
                  Titulo
                </label>

                <input
                  type="text"
                  placeholder="Titulo"
                  {...register("title", {
                    required: "Este campo é obrigatório",
                    maxLength: 100,
                  })}
                  className="block w-full p-2 border rounded-md"
                />
                <p className="text-xs text-gray-500">
                  Titulo para o banner, será exibido somente no painel
                  administrativo
                </p>
                {errors.title && (
                  <span className="text-red-500">{errors.title.message}</span>
                )}
              </div>

              <div className="w-1/2">
                <label
                  htmlFor="text"
                  className="block text-sm font-medium text-gray-700"
                >
                  Descrição
                </label>
                <input
                  type="text"
                  placeholder="Descrição..."
                  {...register("text", {
                    required: "Este campo é obrigatório",
                  })}
                  className="block w-full p-2 border rounded-md"
                />
                <p className="text-xs text-gray-500">
                  Descrição para o banner, será exibido somente no painel
                  administrativo
                </p>
                {errors.text && (
                  <span className="text-red-500">{errors.text.message}</span>
                )}
              </div>
            </div>
          </>
        )}

        {type !== "CAROUSEL" && (
          <>
            <div className="w-full">
              <label
                htmlFor="text"
                className="block text-sm font-medium text-gray-700"
              >
                Link
              </label>
              <input
                type="text"
                placeholder="Ex:
                produtos?category=categoryID"
                {...register("href")}
                className="block w-full p-2 border rounded-md"
              />
              <p className="text-xs text-gray-500">
                Caminho de redirecionamento, este categoryID pode ser retirado
                do próprio site, ou na seção de categorias.
              </p>
              {errors.href && (
                <span className="text-red-500">{errors.href.message}</span>
              )}
            </div>
            <div className="flex gap-2 w-full">
              <div className="w-1/2">
                <label
                  htmlFor="imgUrl"
                  className="block text-sm font-medium text-gray-700"
                >
                  Imagem Desktop (2880x1080)
                </label>
                <input
                  type="text"
                  placeholder="Ex: https://i.imgur.com/LIz7pxN.jpg"
                  {...register("imgUrl")}
                  className="block w-full p-2 border rounded-md"
                />
                <p className="text-xs text-gray-500">
                  Link da imagem para desktop
                </p>
                {errors.imgUrl && (
                  <span className="text-red-500">{errors.imgUrl.message}</span>
                )}
                {standardImgUrl?.trim() && (
                  <div className="mt-2">
                    <img
                      src={standardImgUrl}
                      alt="Preview Desktop"
                      className="max-w-xs h-24 object-cover rounded border border-gray-300"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="w-1/2">
                <label
                  htmlFor="imgUrlMobile"
                  className="block text-sm font-medium text-gray-700"
                >
                  Imagem Mobile (800x839)
                </label>
                <input
                  type="text"
                  placeholder="Ex: https://i.imgur.com/mobile-banner.jpg"
                  {...register("imgUrlMobile")}
                  className="block w-full p-2 border rounded-md"
                />
                <p className="text-xs text-gray-500">
                  Link da imagem para mobile
                </p>
                {errors.imgUrlMobile && (
                  <span className="text-red-500">
                    {errors.imgUrlMobile.message}
                  </span>
                )}
                {standardImgUrlMobile?.trim() && (
                  <div className="mt-2">
                    <img
                      src={standardImgUrlMobile}
                      alt="Preview Mobile"
                      className="max-w-xs h-24 object-cover rounded border border-gray-300"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full">
              <div className="w-full">
                <label
                  htmlFor="videoUrl"
                  className="block text-sm font-medium text-gray-700"
                >
                  Video
                </label>
                <input
                  type="text"
                  placeholder="Ex: ID do Vimeo (123456789)"
                  {...register("videoUrl")}
                  className="block w-full p-2 border rounded-md"
                />
                <p className="text-xs text-gray-500">
                  Insira o ID do vídeo do Vimeo
                </p>
                {errors.videoUrl && (
                  <span className="text-red-500">
                    {errors.videoUrl.message}
                  </span>
                )}
                {videoUrl?.trim() && vimeoVideoId && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-700 mb-2">
                      Preview do Vídeo Vimeo:
                    </p>
                    <div
                      className="relative w-full max-w-2xl"
                      style={{ paddingBottom: "56.25%" }}
                    >
                      <iframe
                        src={`https://player.vimeo.com/video/${vimeoVideoId}`}
                        title="Preview vídeo Vimeo"
                        className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg border border-gray-300"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <button
          type="submit"
          className="px-4 py-2 bg-blue-500 text-white rounded-md cursor-pointer"
        >
          Salvar
        </button>
      </form>

      <Alerts
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        type={warning.type}
        message={warning.message}
      />
    </>
  );
}
